<template>
    <be-form-group v-bind="widgetProps">
        <div class="d-sm-none">
            <be-loading :active="!options">
                <select :name="name" class="form-control" multiple :disabled="disabled" @change="onChange">
                    <option value="" v-if="!options">Lade Optionen …</option>
                    <option
                        v-for="option in options"
                        :value="option.value"
                        :selected="value.includes(option.value)"
                        :key="option.value"
                        v-else
                    >{{ option.label }}
                    </option>
                </select>
            </be-loading>
            <button type="button" class="btn btn-secondary btn-space w-100 mt-2" @click="createNewOption" v-if="isCoach && !disabled">
                <i class="icon mdi mdi-plus"></i>
                Eintrag hinzufügen
            </button>
            <a
                :href="`mailto:support@digital.fitness?subject=${encodeURI(subject)}`"
                class="btn btn-secondary btn-space w-100 mt-2"
                v-else-if="!disabled"
            >
                <i class="icon mdi mdi-plus"></i>
                Eintrag vorschlagen
            </a>
        </div>

        <div class="ms-container d-none d-sm-block">
            <div class="ms-selectable">
                <input
                    :name="name"
                    type="text"
                    placeholder="Suchen"
                    class="form-control search-input"
                    :disabled="disabled || !options"
                    autocomplete="off"
                    v-model="selectableSearch"
                >
                <be-loading :active="!options">
                    <ul class="ms-list">
                        <li
                            :class="{ 'ms-elem-selectable': true, 'disabled': disabled }"
                            :key="option.value"
                            v-for="option in selectableOptions"
                            @click="addOption(option.value)"
                            @mouseover="selectionFocus"
                            @mouseout="selectionBlur"
                        >{{ option.label }}</li>
                    </ul>
                </be-loading>

                <button type="button" class="btn btn-secondary btn-space w-100 mt-2" @click="createNewOption" v-if="isCoach && !disabled">
                    <i class="icon mdi mdi-plus"></i>
                    Eintrag hinzufügen
                </button>
                <a
                    :href="`mailto:support@digital.fitness?subject=${encodeURI(subject)}`"
                    class="btn btn-secondary btn-space w-100 mt-2"
                    v-else-if="!disabled"
                >
                    <i class="icon mdi mdi-plus"></i>
                    Eintrag vorschlagen
                </a>
            </div>

            <div class="ms-selection">
                <input
                    type="text"
                    placeholder="Suchen"
                    class="form-control search-input"
                    :disabled="disabled || !options"
                    v-model="selectionSearch"
                >
                <ul class="ms-list">
                    <li
                        :class="{ 'ms-elem-selection': true, 'disabled': disabled }"
                        :key="option.value"
                        v-for="option in selectionOptions"
                        @click="removeOption(option.value)"
                        @mouseover="selectionFocus"
                        @mouseout="selectionBlur"
                    >{{ option.label }}</li>
                </ul>
            </div>
        </div>

        <be-modal :title="subject" with-footer effect="1" :show="optionVisible">
            <text-field label="Bezeichnung der Option:" autofocus v-model="optionLabel" :validation="$v.optionLabel"/>
            <template #footer>
                <submit-button type="button" color="secondary" @click="optionVisible = false">Abbrechen</submit-button>
                <submit-button type="button" color="success" @click="saveNewOption">Hinzufügen</submit-button>
            </template>
        </be-modal>
    </be-form-group>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { required } from 'vuelidate/lib/validators';
    import { withParams } from 'vuelidate/lib/validators/common';

    import widget from '../../mixins/widget';
    import errorHandler from '../../mixins/errorHandler';
    import CheckOptions from '../../data/CheckOptions';
    import CheckOption from '../../models/CheckOption';

    export default {
        mixins: [widget, errorHandler],

        props: {
            value: {
                type: Array,
                default: () => [],
            },
            optionsKey: {
                type: String,
                required: true,
            },
            subject: String,
        },

        data: () => ({
            options: null,
            selectableSearch: '',
            selectionSearch: '',

            optionVisible: false,
            optionLabel: '',
        }),

        validations: {
            optionLabel: {
                required,
                duplicate(value) {
                    const vm = this;

                    return (withParams(
                        { message: 'Eine Option mit diesem Namen ist bereits vorhanden.' },
                        async () => value === '' || (await CheckOptions.all()).find(o => o.value === value && o.key === vm.optionsKey) === undefined,
                    ))();
                },
            },
        },

        computed: {
            ...mapGetters('user', ['isCoach']),

            selectableOptions() {
                if (!this.options) {
                    return [];
                }

                const options = [];
                const search = this.selectableSearch.toLowerCase();

                this.options.forEach((option) => {
                    if (!this.value.includes(option.value)
                        && (!this.selectableSearch || option.label.toLowerCase().includes(search))
                    ) {
                        options.push(option);
                    }
                });

                return options;
            },

            selectionOptions() {
                if (!this.options) {
                    return [];
                }

                const options = [];
                const search = this.selectionSearch.toLowerCase();

                this.options.forEach((option) => {
                    if (this.value.includes(option.value)
                        && (!this.selectionSearch || option.label.toLowerCase().includes(search))
                    ) {
                        options.push(option);
                    }
                });

                return options;
            },
        },

        methods: {
            onChange(event) {
                const selection = Array.from(this.value);
                const options = event.target && event.target.options;

                for (let i = 0, iLen = options.length; i < iLen; i += 1) {
                    const value = options[i].value || options[i].text;

                    if (options[i].selected && !selection.includes(value)) {
                        selection.push(value);
                    } else if (!options[i].selected && selection.includes(value)) {
                        selection.shift(selection.indexOf(value), 1);
                    }
                }

                this.$emit('input', selection);
            },

            selectionFocus(e) {
                if (this.disabled) {
                    return;
                }

                e.target.classList.add('ms-hover');
            },

            selectionBlur(e) {
                if (this.disabled) {
                    return;
                }

                e.target.classList.remove('ms-hover');
            },

            addOption(value) {
                if (this.disabled) {
                    return;
                }

                const selection = Array.from(this.value);
                selection.push(value);
                this.$emit('input', selection);
            },

            removeOption(value) {
                if (this.disabled) {
                    return;
                }

                const selection = Array.from(this.value);
                selection.shift(selection.indexOf(value), 1);
                this.$emit('input', selection);
            },

            createNewOption() {
                this.optionLabel = '';
                this.optionsSaving = false;
                this.optionVisible = true;
            },

            async saveNewOption() {
                this.$v.$reset();
                this.$v.$touch();

                if (this.$v.$pending || this.$v.$error) {
                    return;
                }

                this.options = null;
                this.optionVisible = false;

                try {
                    await new CheckOption({
                        key: this.optionsKey,
                        value: this.optionLabel,
                    }).save();

                    this.$store.dispatch('notifications/add', {
                        color: 'success',
                        title: this.subject,
                        description: 'Die neue Option wurde gespeichert.',
                    });
                } catch (err) {
                    this.catchError(this.subject, 'Die Option konnte nicht gespeichert werden!', err);
                }

                CheckOptions.reload();
                this.options = await CheckOptions.getOptionsForKey(this.optionsKey);
            },
        },

        async mounted() {
            this.options = await CheckOptions.getOptionsForKey(this.optionsKey);
        },
    };
</script>

<style rel="stylesheet/scss" lang="scss">
    .ms-container {
        .ms-selectable,
        .ms-selection {
            li.disabled {
                color: inherit;
            }
        }
    }
</style>
